import type { CSSProperties, ReactNode } from 'react';

export type BadgeKind =
  | 'DRAFT'
  | 'APPROVED'
  | 'SENT'
  | 'FULFILLED'
  | 'CANCELLED'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'PROMOTED'
  | 'new'
  | 'update'
  | 'unchanged'
  | 'INFO'
  | 'WARNING'
  | 'CRITICAL';

const VI_LABEL: Record<string, string> = {
  DRAFT: 'Nháp',
  APPROVED: 'Đã duyệt',
  SENT: 'Đã gửi',
  FULFILLED: 'Hoàn tất',
  CANCELLED: 'Đã hủy',
  ACTIVE: 'Hoạt động',
  INACTIVE: 'Tạm dừng',
  PROMOTED: 'Đã chốt',
  PENDING: 'Chờ duyệt',
  REJECTED: 'Từ chối',
  SUBMITTED: 'Đã gửi',
  OVERDUE: 'Trễ hạn',
  new: 'Mới',
  update: 'Sửa',
  unchanged: 'Giữ nguyên',
  INFO: 'Thông tin',
  WARNING: 'Cảnh báo',
  CRITICAL: 'Nghiêm trọng',
  MEGA_SALE: 'Sale lớn',
  PAYDAY: 'Ngày lương',
  MID_MONTH: 'Giữa tháng',
  MINI_SPIKE: 'Sale nhỏ',
  DOUBLE_DAY: 'Double day',
  NORMAL: 'Bình thường',
  KEY: 'Trọng điểm',
  MAJOR: 'Lớn',
  STANDARD: 'Phổ thông',
  GROWTH: 'Tăng trưởng',
  LONGTAIL: 'Long-tail',
  FORECAST: 'Dự báo',
  ACTUAL: 'Thực tế',
  UNCERTAIN: 'Chưa chắc',
  OFFICIAL: 'Chính thức',
  TEMP: 'Thời vụ',
};

export function viLabel(key: unknown): string {
  if (typeof key === 'string' && key in VI_LABEL) return VI_LABEL[key]!;
  return typeof key === 'string' ? key : String(key ?? '');
}

const STATUS_COLORS: Record<string, { bg: string; fg: string; dot: string }> = {
  DRAFT: { bg: '#f5f5f5', fg: '#4a4a4a', dot: '#99a1af' },
  APPROVED: { bg: '#eef2ff', fg: '#0b43ea', dot: '#0b43ea' },
  SENT: { bg: '#eff6ff', fg: '#1e40af', dot: '#1e40af' },
  FULFILLED: { bg: '#f0fdf4', fg: '#15803d', dot: '#00af4e' },
  CANCELLED: { bg: '#f5f5f5', fg: '#525252', dot: '#a3a3a3' },
  ACTIVE: { bg: '#f0fdf4', fg: '#15803d', dot: '#00af4e' },
  INACTIVE: { bg: '#f5f5f5', fg: '#737373', dot: '#a3a3a3' },
  PROMOTED: { bg: '#f0fdf4', fg: '#15803d', dot: '#00af4e' },
  new: { bg: '#eef2ff', fg: '#0b43ea', dot: '#0b43ea' },
  update: { bg: '#fefce8', fg: '#92400e', dot: '#d97706' },
  unchanged: { bg: '#f5f5f5', fg: '#737373', dot: '#a3a3a3' },
  INFO: { bg: '#eff6ff', fg: '#1e40af', dot: '#2563eb' },
  WARNING: { bg: '#fefce8', fg: '#92400e', dot: '#d97706' },
  CRITICAL: { bg: '#fef2f2', fg: '#b91c1c', dot: '#dc2626' },
};

interface BadgeProps {
  kind?: string;
  children?: ReactNode;
  dot?: boolean;
  style?: CSSProperties;
  size?: 'sm' | 'md';
}

export function Badge({ kind, children, dot, style, size = 'md' }: BadgeProps) {
  const c = (kind && STATUS_COLORS[kind]) || {
    bg: 'var(--bx-surface-2)',
    fg: 'var(--bx-text-body)',
    dot: '#a3a3a3',
  };
  const labelText = typeof children === 'string' ? viLabel(children) : children ?? viLabel(kind);
  const isUpper = typeof labelText === 'string' && labelText.length < 12;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: size === 'sm' ? 18 : 22,
        padding: size === 'sm' ? '0 8px' : '0 10px',
        borderRadius: 9999,
        fontSize: size === 'sm' ? 10.5 : 11,
        fontWeight: 600,
        letterSpacing: '0.01em',
        textTransform: isUpper ? 'uppercase' : 'none',
        background: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {dot !== false && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: c.dot,
          }}
        />
      )}
      {labelText}
    </span>
  );
}
